import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { v4 as uuid } from 'uuid';

import { Body } from '@cognite/cogs-core-v11/tokens/typography/typography.tsx';
import { Button } from '@cognite/cogs-core-v11/components/button/button.tsx';
import { DeleteIcon } from '@cognite/cogs-icons-v11/system-icons/DeleteIcon.tsx';
import { Flex } from '@cognite/cogs-core-v11/components/flex/flex.tsx';
import { Input } from '@cognite/cogs-core-v11/components/input/input.tsx';

import { TextInputModal } from '../../template/text-input-modal/text-input-modal';
import { ComponentContainer } from '../../ui/component/component-container/component-container';
import { useUpsertAppConfigMutation } from '../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../hooks/use-query/use-app-config-query';
import { SaveConfigurationButton } from '../save-configuration/save-configuration-button';
import type { AppConfig } from '../types';

import * as S from './elements';

interface Props {
  hasAppConfigPermission: boolean;
}

export const AppConfigActivities: FC<Props> = ({ hasAppConfigPermission }) => {
  const [overviewCardFilters, setOverviewCardFilters] = useState<
    Record<string, { externalId: string; value: string }[]>
  >({});

  const [showModal, setShowModal] = useState(false);

  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();
  const activityConfiguration = appConfig?.featureConfiguration?.activities;

  useEffect(() => {
    if (isSuccess && activityConfiguration) {
      if (activityConfiguration.overviewCard?.filters) {
        setOverviewCardFilters(activityConfiguration.overviewCard?.filters);
      }
    }
  }, [isSuccess, activityConfiguration, appConfig]);

  const hasConfigChanged =
    overviewCardFilters !==
    (activityConfiguration?.overviewCard?.filters ?? '');

  const handleSave = async () => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        activities: {
          ...appConfig.featureConfiguration?.activities,
          overviewCard: {
            ...appConfig.featureConfiguration?.activities?.overviewCard,
            filters: overviewCardFilters,
          },
        },
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const handleOnFilterValueChange = (
    field: string,
    value: string,
    externalId: string
  ) => {
    setOverviewCardFilters((prev) => ({
      ...prev,
      [field]: prev[field].map((prevFilterValue) => {
        if (prevFilterValue.externalId === externalId) {
          return {
            ...prevFilterValue,
            value,
          };
        }
        return prevFilterValue;
      }),
    }));
  };

  const handleDeleteFilterValue = (field: string, externalId: string) => {
    setOverviewCardFilters((prev) => ({
      ...prev,
      [field]: prev[field].filter(
        (prevFilterValue) => prevFilterValue.externalId !== externalId
      ),
    }));
  };

  const handleDeleteField = (field: string) => {
    setOverviewCardFilters((prev) => {
      const state = { ...prev };
      delete state[field];
      return state;
    });
  };

  const handleAddField = (field: string) => {
    setShowModal(false);
    setOverviewCardFilters({
      ...overviewCardFilters,
      [field]: [],
    });
  };

  const activityConfigs = (
    <Flex direction="column" gap={8}>
      <Body size="x-small">
        Configure how the activities shall display data in the asset activities
        card or activities full page. Fields are configured by its CDF field
        name, eg *id*, *name* or *metadata.title*.
      </Body>

      <Body size="x-small" strong>
        Overview Card Filters
      </Body>
      <Flex gap={16} direction="column">
        {Object.keys(overviewCardFilters).map((field) => (
          <Flex gap={8} key={field} direction="column">
            <Flex gap={8}>
              <Body size="x-small" strong>
                Field name:
              </Body>
              <Body size="x-small">{field}</Body>
              <Button
                icon={<DeleteIcon />}
                type="destructive"
                onClick={() => handleDeleteField(field)}
              />
            </Flex>

            <Flex gap={8}>
              <Body size="x-small" strong>
                Accepted values:
              </Body>
              {overviewCardFilters[field].map((filter) => (
                <Flex key={filter.externalId} gap={8}>
                  <Input
                    disabled={!hasAppConfigPermission}
                    value={filter.value}
                    onChange={(e) =>
                      handleOnFilterValueChange(
                        field,
                        e.target.value,
                        filter.externalId
                      )
                    }
                  />
                  <Button
                    icon={<DeleteIcon />}
                    type="destructive"
                    onClick={() =>
                      handleDeleteFilterValue(field, filter.externalId)
                    }
                  />
                </Flex>
              ))}

              <Button
                onClick={() =>
                  setOverviewCardFilters({
                    ...overviewCardFilters,
                    [field]: [
                      ...overviewCardFilters[field],
                      { externalId: uuid(), value: '' },
                    ],
                  })
                }
              >
                Add filter value
              </Button>
            </Flex>
          </Flex>
        ))}
      </Flex>

      <S.FixedWidthButton onClick={() => setShowModal(true)}>
        Add filter field
      </S.FixedWidthButton>
    </Flex>
  );

  return (
    <ComponentContainer>
      <S.Wrapper>
        <Flex direction="column" gap={16}>
          {activityConfigs}
          <SaveConfigurationButton
            onSave={handleSave}
            isDisabled={!hasConfigChanged}
            isLoading={isLoading}
          />
        </Flex>
      </S.Wrapper>

      <TextInputModal
        size="small"
        visible={showModal}
        title="Add new filter field"
        inputLabel="Field name"
        okText="Add field"
        onSubmit={handleAddField}
        onCancel={() => setShowModal(false)}
      >
        {null}
      </TextInputModal>
    </ComponentContainer>
  );
};
